import { useEffect, useState, useRef } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, FileText, DownloadSimple, ClockCounterClockwise, UploadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = ["Architectural Plans","Structural Plans","MEP Plans","Civil Plans","Specifications","Revisions","Addenda","Permit Documents","As-Built Drawings"];
const EMPTY_GRAPHIC = "https://images.pexels.com/photos/6615086/pexels-photo-6615086.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function DocumentsTab({ projectId, onChange }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ category: "Architectural Plans", name: "", notes: "", document_id: "" });
  const fileRef = useRef();

  const load = () => api.get(`/projects/${projectId}/documents`).then(({ data }) => setDocs(data.documents)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const openNew = (documentId = "", name = "") => {
    setForm({ category: "Architectural Plans", name, notes: "", document_id: documentId });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Please select a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", form.category);
      fd.append("name", form.name || file.name);
      fd.append("notes", form.notes);
      fd.append("document_id", form.document_id);
      fd.append("file", file);
      await api.post(`/projects/${projectId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(form.document_id ? "New version uploaded" : "Document uploaded");
      setOpen(false); await load(); onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const download = (docId, version) => {
    const token = localStorage.getItem("ch_token");
    window.open(`${API}/documents/${docId}/download?version=${version}&auth=${token}`, "_blank");
  };

  const remove = async (id) => {
    await api.delete(`/projects/${projectId}/documents/${id}`);
    toast.success("Deleted"); await load(); onChange?.();
  };

  const grouped = CATEGORIES.map((c) => ({ category: c, items: docs.filter((d) => d.category === c) })).filter((g) => g.items.length);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline text-primary mb-1">Phase 2 · Document Control</div>
          <h2 className="font-heading font-bold text-xl tracking-tight">Project Plans</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openNew()} data-testid="upload-document-button" className="rounded-none bg-primary hover:bg-primary/90 text-white gap-2">
              <UploadSimple size={18} weight="bold" /> Upload Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-heading font-black tracking-tight">{form.document_id ? "Upload New Version" : "Upload Document"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="document-form">
              {!form.document_id && (
                <div className="space-y-2">
                  <Label className="overline">Category *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="document-category-select" className="rounded-none h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2"><Label className="overline">Document Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="document-name-input" className="rounded-none h-11" placeholder="e.g. Level 1 Floor Plan" /></div>
              <div className="space-y-2"><Label className="overline">File *</Label><Input ref={fileRef} type="file" data-testid="document-file-input" className="rounded-none h-11 file:mr-3 file:border-0 file:bg-secondary file:px-3 file:py-1" /></div>
              <div className="space-y-2"><Label className="overline">Revision Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="document-notes-input" className="rounded-none" rows={2} /></div>
              <Button type="submit" disabled={busy} data-testid="document-submit-button" className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white">
                {busy ? "Uploading…" : "Upload"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {docs.length ? (
        <div className="space-y-6">
          {grouped.map((g) => (
            <div key={g.category}>
              <div className="overline text-muted-foreground mb-2">{g.category} · {g.items.length}</div>
              <div className="border border-border bg-card divide-y divide-border">
                {g.items.map((d) => (
                  <Accordion type="single" collapsible key={d.id}>
                    <AccordionItem value={d.id} className="border-0">
                      <div className="flex items-center gap-3 px-4 py-3" data-testid={`document-row-${d.id}`}>
                        <FileText size={22} weight="duotone" className="text-primary shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{d.name}</div>
                          <div className="text-xs text-muted-foreground">v{d.current_version} · {d.versions?.[d.versions.length - 1]?.uploaded_by} · {d.downloads?.length || 0} downloads</div>
                        </div>
                        <button onClick={() => download(d.id, d.current_version)} data-testid={`download-doc-${d.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-150" title="Download current"><DownloadSimple size={20} /></button>
                        <button onClick={() => openNew(d.id, d.name)} data-testid={`new-version-${d.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-150" title="Upload new version"><Plus size={20} /></button>
                        <button onClick={() => remove(d.id)} data-testid={`delete-doc-${d.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-150"><Trash size={18} /></button>
                        <AccordionTrigger className="p-0 hover:no-underline" data-testid={`history-toggle-${d.id}`}><ClockCounterClockwise size={20} className="text-muted-foreground" /></AccordionTrigger>
                      </div>
                      <AccordionContent className="px-4 pb-3">
                        <div className="overline text-muted-foreground mb-2">Version History</div>
                        <div className="space-y-1">
                          {[...d.versions].reverse().map((v) => (
                            <div key={v.version} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-1.5 py-0.5 ${v.version === d.current_version ? "bg-primary text-white" : "bg-secondary"}`}>v{v.version}</span>
                                <span className="text-muted-foreground">{v.filename}</span>
                                {v.notes && <span className="text-muted-foreground italic">— {v.notes}</span>}
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-muted-foreground">{new Date(v.uploaded_at).toLocaleDateString()} · {v.uploaded_by}</span>
                                <button onClick={() => download(d.id, v.version)} className="text-primary hover:underline text-xs">Download</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border border-dashed p-8 text-center">
          <img src={EMPTY_GRAPHIC} alt="Blueprints" className="w-48 h-32 object-cover mx-auto mb-4 grayscale" />
          <p className="text-muted-foreground">No documents yet. Upload architectural, structural, MEP and civil plans to begin document control.</p>
        </div>
      )}
    </div>
  );
}
